import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Modal, TextField, Typography, DragAndDrop } from '@ann/web-components';
import { observer } from 'mobx-react';
import { useStore } from '../../../../../../../hooks';
import { useToast } from '../../../../../../../components/toast';
import { useMediaQueries } from '../../../../../../../utils/mediaQueries';
import { ErrorMessage, ModalBodyContent, UploadPhotoContainer } from './CreateWorkspaceModal.styled';

const CreateWorkspaceModal = ({
  size = "medium",
  isOpen,
  onClose,
  disabled = false
}) => {
  const { workspaceStore } = useStore();
  const toast = useToast();
  const { isIpadAirPortrait, isIpadProPortrait } = useMediaQueries();
  const [workspaceName, setWorkspaceName] = useState('');
  const [owner, setOwner] = useState('');
  const [contactName, setContactName] = useState('');
  const [address, setAddress] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const fileInputRef = useRef(null);
  
  // Calculate modal width: 90% for iPad portrait, 900px otherwise
  const modalWidth = useMemo(() => {
    if (isIpadAirPortrait || isIpadProPortrait) {
      return '90%';
    }
    return '900px';
  }, [isIpadAirPortrait, isIpadProPortrait]);
  
  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  const isFieldEmpty = !workspaceName.trim();
  const isOwnerEmpty = !owner.trim();
  const isOwnerInvalid = owner.trim() && !emailRegex.test(owner.trim());
  const isContactNameEmpty = !contactName.trim();
  const showNameError = isFieldEmpty;
  const showOwnerError = isOwnerEmpty || isOwnerInvalid;
  const showContactNameError = isContactNameEmpty;
  const isFormValid = !isFieldEmpty && !isOwnerEmpty && !isOwnerInvalid && !isContactNameEmpty;
  const isCreating = workspaceStore.isLoading;
  const previousLoadingRef = useRef(false);

  // Show toast notifications based on store state
  useEffect(() => {
    // Only process when loading transitions from true to false
    if (previousLoadingRef.current && !isCreating && isOpen) {
      // Check for success first
      if (workspaceStore.selectedWorkspace && !workspaceStore.error) {
        // Success: workspace was created
        // Priority: workspaceName/workspace_name (new) > tenantName/tenant_name (legacy) > name (generic) > fallback
        const workspaceName = workspaceStore.selectedWorkspace.workspaceName || workspaceStore.selectedWorkspace.workspace_name || workspaceStore.selectedWorkspace.tenantName || workspaceStore.selectedWorkspace.tenant_name || workspaceStore.selectedWorkspace.name || 'Workspace';
        toast.success({
          message: `${workspaceName} was created successfully`,
          canClose: true
        });
        // Reset form and close modal
        setWorkspaceName('');
        setOwner('');
        setContactName('');
        setAddress('');
        setImagePreview(null);
        setLogoFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        workspaceStore.clearError();
        onClose();
      } 
      // Check for error
      else if (workspaceStore.error) {
        // Error: show error toast
        const errorMessage = workspaceStore.errorMessage || 'An error occurred while creating workspace';
        toast.error({
          message: errorMessage,
          canClose: true
        });
      }
    }
    previousLoadingRef.current = isCreating;
  }, [isCreating, workspaceStore.selectedWorkspace, workspaceStore.error, workspaceStore.errorMessage, isOpen, toast, onClose]);

  const handleSave = async () => {
    if (!isFormValid) {
      return;
    }

    try {
      // Generate slug from name: lowercase and replace spaces with hyphens
      const trimmedName = workspaceName.trim();
      const slug = trimmedName.toLowerCase().replace(/\s+/g, '-');
      
      const workspaceData = {
        name: trimmedName,
        slug: slug,
        contact_email: owner.trim() || '',
        contact_name: contactName.trim() || '',
        phone_number: '+1234567890',
        billing_plan: 'basic',
        organization_id: 1,
        contact_address: address.trim() || '',
        logoFile: logoFile || null, // Include logo file if available
      };

      await workspaceStore.createWorkspace(workspaceData);
      
      // Toast notifications and modal close are handled by useEffect
      
    } catch (error) {
      // Error handling is done in useEffect based on store state
      console.error('Error creating workspace:', error);
    }
  };

  const handleClose = () => {
    setWorkspaceName('');
    setOwner('');
    setContactName('');
    setAddress('');
    setImagePreview(null);
    setLogoFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    workspaceStore.clearError();
    onClose();
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    const file = e.dataTransfer.files[0];
    if (file) {
      setLogoFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width={modalWidth} size={size}>
      <Modal.Header>
        <Modal.Header.Title>
          Create Workspace
        </Modal.Header.Title>
        <Modal.Header.Description>
          <Typography category="body" size="medium" weight="regular" color="body">
            Add a new organisation, division, or workspace to your enviroment.
          </Typography>
        </Modal.Header.Description>
      </Modal.Header>
      <Modal.Body>
        <ModalBodyContent>
          <TextField 
          label="Name"
          size="small" 
          placeholder="eg: Atlas Financial Group"
          value={workspaceName}
          onChange={(value) => setWorkspaceName(value)}
          required={true}
          />
          {showNameError && (
          <ErrorMessage>Workspace name is required</ErrorMessage>
          )}
          
          <TextField 
          label="Owner"
          size="small" 
          placeholder="eg: owner@company.com"
          value={owner}
          onChange={(value) => setOwner(value)}
          required={true}
          />
          {showOwnerError && (
          <ErrorMessage>
            {isOwnerEmpty ? 'Owner email is required' : 'Please enter a valid email address'}
          </ErrorMessage>
          )}

          <TextField 
            label="Contact Name"
            size="small" 
            placeholder="eg: Contact Name"
            value={contactName}
            onChange={(value) => setContactName(value)}
            required={true}
          />
          {showContactNameError && (
            <ErrorMessage>Contact name is required</ErrorMessage>
          )}

          <TextField 
            label="Address / Location (Optional)"
            size="small" 
            placeholder="Enter full address"
            value={address}
            onChange={(value) => setAddress(value)}
          />

          <UploadPhotoContainer>
            <Typography category="label" size="small" weight="bold">
              Logo (Optional)
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.svg"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <DragAndDrop
              className="drag-and-drop"
              uploadType="images"
              acceptedFileTypes={['.png', '.jpg', '.jpeg', '.svg']}
              onUploadClick={handleUploadClick}
              onDrop={handleDrop}
              width="100%"
              imagePreview={imagePreview}
            />
          </UploadPhotoContainer>

        </ModalBodyContent>
      </Modal.Body>
      <Modal.Actions align="end">
        <Modal.Button
          label="Cancel"
          onClick={handleClose}
          type="secondary"
          disabled={isCreating}
        />
        <Modal.Button
          label={isCreating ? "Creating..." : "Create"}
          onClick={handleSave}
          disabled={disabled || isCreating || !isFormValid}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default observer(CreateWorkspaceModal);

